import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Apple, CheckCircle2, MousePointerClick, Smartphone, XCircle } from 'lucide-react';
import { Link } from 'react-router';
import { EmptyState } from '@/components/shared/empty-state';
import { SectionCard } from '@/components/shared/section-card';
import { StatCard } from '@/components/shared/stat-card';
import { StatusBadge, ToneBadge } from '@/components/shared/tone-badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api } from '@/lib/api';
import { fmtDate, fmtNum } from '@/lib/format';
import type { Page, Send } from '@/lib/types';
import { useAppCtx } from './app-layout';

type Split = { android: number; ios: number; unknown: number };
type Summary = {
  accepted: number; failed: number; clicked: number; received: null;
  by_platform?: { accepted: Split; failed: Split; clicked: Split };
};

/** Android / iOS counts under a tile; "unknown" only appears for older clicks stored without a platform. */
function PlatformSplit({ split }: { split: Split | undefined }) {
  if (!split) return null;
  return (
    <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <span className="inline-flex items-center gap-1"><Smartphone className="size-3.5" /> Android <b className="font-medium tabular-nums text-foreground">{fmtNum(split.android)}</b></span>
      <span className="inline-flex items-center gap-1"><Apple className="size-3.5" /> iOS <b className="font-medium tabular-nums text-foreground">{fmtNum(split.ios)}</b></span>
      {split.unknown > 0 && <span className="tabular-nums">Unknown {fmtNum(split.unknown)}</span>}
    </span>
  );
}

export function Overview() {
  const { app, env } = useAppCtx();
  const environment = app.environments.find((e) => e.environment === env);
  const summary = useQuery({ queryKey: ['summary', app.id, env], queryFn: () => api<Summary>(`/admin/v1/apps/${app.id}/analytics/summary`, { query: { environment: env } }), refetchInterval: 30_000, placeholderData: keepPreviousData });
  const sends = useQuery({ queryKey: ['sends', app.id, env, 'recent'], queryFn: () => api<Page<Send>>(`/admin/v1/apps/${app.id}/sends`, { query: { environment: env, limit: 8 } }), placeholderData: keepPreviousData });

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard label="Accepted (24 h)" value={fmtNum(summary.data?.accepted)} hint={<PlatformSplit split={summary.data?.by_platform?.accepted} />} icon={CheckCircle2} accent="bg-success/10 text-success" loading={summary.isLoading} />
        <StatCard label="Failed (24 h)" value={fmtNum(summary.data?.failed)} hint={<PlatformSplit split={summary.data?.by_platform?.failed} />} icon={XCircle} accent="bg-destructive/10 text-destructive" loading={summary.isLoading} />
        <StatCard label="Clicked (24 h)" value={fmtNum(summary.data?.clicked)} hint={<PlatformSplit split={summary.data?.by_platform?.clicked} />} icon={MousePointerClick} accent="bg-primary/10 text-primary" loading={summary.isLoading} />
      </div>

      <SectionCard title="Credential health" description={`Push providers configured for ${env}.`}>
        {!environment ? (
          <EmptyState title={`No ${env} environment`} action={<Button asChild variant="outline" size="sm"><Link to={`/apps/${app.id}/credentials?env=${env}`}>Set up credentials</Link></Button>} />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {(['android', 'ios'] as const).map((p) => {
              const c = environment[p];
              const Icon = p === 'android' ? Smartphone : Apple;
              return (
                <div key={p} className="flex items-center justify-between rounded-lg border p-4">
                  <span className="inline-flex items-center gap-3 text-sm font-medium">
                    <span className="grid size-8 place-items-center rounded-md bg-muted"><Icon className="size-4" /></span>
                    {p === 'android' ? 'Android (FCM)' : 'iOS (APNs)'}
                  </span>
                  {!c ? <ToneBadge>not configured</ToneBadge> : !c.enabled ? <ToneBadge tone="slate">disabled</ToneBadge> : c.verified_at ? <ToneBadge tone="green" dot>verified</ToneBadge> : <ToneBadge tone="amber" dot>not verified</ToneBadge>}
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>

      <SectionCard className={sends.isPlaceholderData ? "opacity-60 transition-opacity" : "transition-opacity"} title="Recent notifications" actions={<Button asChild variant="ghost" size="sm"><Link to={`/apps/${app.id}/sends?env=${env}`}>View all</Link></Button>} flush>
        {sends.data?.data.length === 0 ? <EmptyState title="No notifications yet" /> : (
          <Table>
            <TableHeader>
              <TableRow><TableHead className="pl-5">Status</TableHead><TableHead>Title</TableHead><TableHead>Targets</TableHead><TableHead>Accepted</TableHead><TableHead>Failed</TableHead><TableHead className="pr-5">Created</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {sends.data?.data.map((s) => (
                <TableRow key={s.id}>
                  <TableCell className="pl-5"><StatusBadge status={s.status} /></TableCell>
                  <TableCell className="font-medium"><div className="max-w-xs truncate">{s.title}</div></TableCell>
                  <TableCell className="tabular-nums">{s.target_count}</TableCell>
                  <TableCell className="tabular-nums">{s.accepted_count}</TableCell>
                  <TableCell className="tabular-nums">{s.failed_count}</TableCell>
                  <TableCell className="pr-5 text-muted-foreground">{fmtDate(s.created_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </SectionCard>
    </div>
  );
}
